import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "./Navbar";
import FinanceSidebar from "./FinanceSidebar";
import toast from "react-hot-toast";
import axiosInstance from "../config/axios";
import { scannedAccounts } from "./ChartOfAccountsMenu.jsx";
import {
  DEFAULT_OFFICE_EXPENSE_TAX_RATE,
  OFFICE_EXPENSE_OPTIONS,
  isOfficeExpenseTaxExempt,
  parseOfficeExpenseTaxRate,
} from "../utils/officeExpenses";
import { roundTo2, roundTo2Str } from "../utils/numberUtils";

const round2 = roundTo2;

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyAmounts = () =>
  OFFICE_EXPENSE_OPTIONS.reduce((acc, opt) => {
    acc[opt.key] = "";
    return acc;
  }, {});

const AgentExpenses = () => {
  const [agents, setAgents] = useState([]);
  const [recent, setRecent] = useState([]);
  const [totalsByType, setTotalsByType] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);

  const [form, setForm] = useState({
    invoiceDate: todayISO(),
    aeNumber: "",
    agentId: "",
    agentName: "",
    invoiceNumber: "",
    glAccount: "",
    glAccountName: "",
    postDate: todayISO(),
    taxRate: String(Math.round(DEFAULT_OFFICE_EXPENSE_TAX_RATE * 100)),
    amounts: emptyAmounts(),
  });

  const [reportAgentId, setReportAgentId] = useState("");
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");

  const glOptions = useMemo(() => {
    return (scannedAccounts || [])
      .map((a) => ({
        value: a.acct,
        label: `${a.acct} ${a.description}`,
      }))
      .sort((a, b) => {
        const na = parseInt(String(a.value).replace(/\D/g, ""), 10);
        const nb = parseInt(String(b.value).replace(/\D/g, ""), 10);
        if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) {
          return na - nb;
        }
        return String(a.value).localeCompare(String(b.value));
      });
  }, []);

  const loadNextNumbers = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/agent-expenses/next-numbers");
      setForm((prev) => ({
        ...prev,
        aeNumber: res.data.aeNumber || prev.aeNumber,
        invoiceNumber: res.data.invoiceNumber || prev.invoiceNumber,
      }));
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/agent-expenses", {
        params: { limit: 100 },
      });
      setRecent(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load recent agent expenses");
    }
  }, []);

  const loadTotalsByType = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/agent-expenses/totals-by-type");
      setTotalsByType(res.data || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/agents");
      const list = (res.data || []).slice().sort((a, b) => {
        const an = parseInt(a.employeeNo, 10) || 0;
        const bn = parseInt(b.employeeNo, 10) || 0;
        return an - bn;
      });
      setAgents(list);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load agents");
    }
  }, []);

  const loadBalances = useCallback(async (agentId) => {
    if (!agentId) {
      setBalances({});
      return;
    }
    try {
      const res = await axiosInstance.get(
        `/agent-expenses/balances/${encodeURIComponent(agentId)}`,
      );
      const map = {};
      for (const row of res.data?.balances || []) {
        map[row.key] = row;
      }
      setBalances(map);
    } catch (err) {
      console.error(err);
      setBalances({});
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadAgents(),
      loadNextNumbers(),
      loadRecent(),
      loadTotalsByType(),
    ]).finally(() => setLoading(false));
  }, [loadAgents, loadNextNumbers, loadRecent, loadTotalsByType]);

  const computed = useMemo(() => {
    const rate = parseOfficeExpenseTaxRate(form.taxRate);
    let amount = 0;
    let hst = 0;
    const lines = [];
    for (const opt of OFFICE_EXPENSE_OPTIONS) {
      const raw = form.amounts[opt.key];
      const lineAmount = round2(
        parseFloat(String(raw || "").replace(/,/g, "")) || 0,
      );
      if (lineAmount <= 0) continue;
      const taxExempt = isOfficeExpenseTaxExempt(opt.key);
      const tax = taxExempt ? 0 : round2(lineAmount * rate);
      const total = round2(lineAmount + tax);
      lines.push({
        key: opt.key,
        label: opt.label,
        amount: lineAmount,
        tax,
        total,
        taxExempt,
      });
      amount = round2(amount + lineAmount);
      hst = round2(hst + tax);
    }
    return { lines, amount, hst, total: round2(amount + hst), rate };
  }, [form.amounts, form.taxRate]);

  const handleAgentChange = (agentId) => {
    const agent = agents.find((a) => String(a.employeeNo) === String(agentId));
    const name = agent
      ? `${agent.firstName || ""} ${agent.lastName || ""}`.trim()
      : "";
    setForm((prev) => ({
      ...prev,
      agentId,
      agentName: name,
    }));
    loadBalances(agentId);
  };

  const handleGlChange = (value) => {
    const opt = glOptions.find((o) => o.value === value);
    setForm((prev) => ({
      ...prev,
      glAccount: value,
      glAccountName: opt?.label?.replace(/^\d+\s+/, "") || "",
    }));
  };

  const handleAmountChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      amounts: { ...prev.amounts, [key]: value },
    }));
  };

  const resetForm = async () => {
    setForm({
      invoiceDate: todayISO(),
      aeNumber: "",
      agentId: "",
      agentName: "",
      invoiceNumber: "",
      glAccount: "",
      glAccountName: "",
      postDate: todayISO(),
      taxRate: String(Math.round(DEFAULT_OFFICE_EXPENSE_TAX_RATE * 100)),
      amounts: emptyAmounts(),
    });
    setBalances({});
    await loadNextNumbers();
  };

  const handlePost = async () => {
    if (!form.agentId) {
      toast.error("Select an agent");
      return;
    }
    if (!form.glAccount) {
      toast.error("Select a G/L Account");
      return;
    }
    if (computed.lines.length === 0) {
      toast.error("Enter at least one expense amount");
      return;
    }
    try {
      setPosting(true);
      await axiosInstance.post("/agent-expenses", {
        aeNumber: form.aeNumber,
        invoiceNumber: form.invoiceNumber,
        agentId: form.agentId,
        agentName: form.agentName,
        glAccount: form.glAccount,
        glAccountName: form.glAccountName,
        invoiceDate: form.invoiceDate,
        postDate: form.postDate,
        taxRate: form.taxRate,
        lines: computed.lines,
      });
      toast.success(`Posted ${form.aeNumber}`);
      await resetForm();
      await Promise.all([loadRecent(), loadTotalsByType()]);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to post expense");
    } finally {
      setPosting(false);
    }
  };

  const formatDateDisplay = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleDateString("en-US");
  };

  const openPrintReport = async () => {
    if (!reportAgentId) {
      toast.error("Select an agent for the report");
      return;
    }
    try {
      const params = { agentId: reportAgentId };
      if (reportFrom) params.from = reportFrom;
      if (reportTo) params.to = reportTo;
      const [reportRes, companyRes] = await Promise.all([
        axiosInstance.get("/agent-expenses/report", { params }),
        axiosInstance.get("/company-profile").catch((e) => {
          if (e.response?.status === 404) return { data: null };
          throw e;
        }),
      ]);

      const agent = agents.find(
        (a) => String(a.employeeNo) === String(reportAgentId),
      );
      const agentLabel = agent
        ? `${agent.employeeNo} - ${agent.firstName || ""} ${agent.lastName || ""}`.trim()
        : reportAgentId;

      const company =
        companyRes.data || {
          companyName: "Bestway Real Estate Ltd., Brokerage",
          address: "500 St.George Street",
          city: "Moncton",
          province: "NB",
          postalCode: "E1C 1Y3",
        };

      const { expenses, summary } = reportRes.data;
      const periodLabel =
        reportFrom || reportTo
          ? `${reportFrom ? formatDateDisplay(reportFrom) : "…"} – ${
              reportTo ? formatDateDisplay(reportTo) : "…"
            } (by invoice date)`
          : "All invoice dates";

      const colKeys = OFFICE_EXPENSE_OPTIONS.map((o) => o.key);
      const lineTotals = Object.fromEntries(colKeys.map((k) => [k, 0]));
      let hstTotal = 0;
      let totalTotal = 0;
      let appliedTotal = 0;
      let balanceTotal = 0;

      const bodyRows = (expenses || [])
        .map((exp) => {
          const byKey = Object.fromEntries(colKeys.map((k) => [k, 0]));
          for (const line of exp.lines || []) {
            if (byKey[line.key] != null) {
              byKey[line.key] = round2(byKey[line.key] + Number(line.amount || 0));
              lineTotals[line.key] = round2(
                lineTotals[line.key] + Number(line.amount || 0),
              );
            }
          }
          const hst = Number(exp.hst || 0);
          const total = Number(exp.total || 0);
          const applied = Number(exp.applied || 0);
          const balance = round2(total - applied);
          hstTotal = round2(hstTotal + hst);
          totalTotal = round2(totalTotal + total);
          appliedTotal = round2(appliedTotal + applied);
          balanceTotal = round2(balanceTotal + balance);
          const trades = [
            ...new Set(
              (exp.appliedTrades || [])
                .map((t) => t.tradeNumber)
                .filter(Boolean),
            ),
          ].join(", ");

          return `<tr>
            <td>${exp.aeNumber || ""}</td>
            <td>${exp.invoiceNumber || ""}</td>
            <td>${formatDateDisplay(exp.invoiceDate)}</td>
            <td>${trades || ""}</td>
            ${colKeys
              .map(
                (k) =>
                  `<td class="num">$${fmtMoney(byKey[k])}</td>`,
              )
              .join("")}
            <td class="num">$${fmtMoney(hst)}</td>
            <td class="num">$${fmtMoney(total)}</td>
            <td class="num">$${fmtMoney(applied)}</td>
            <td class="num">$${fmtMoney(balance)}</td>
          </tr>`;
        })
        .join("");

      const html = `<!DOCTYPE html>
<html>
<head>
  <title>Agent Expense Report</title>
  <style>
    @page { size: portrait; margin: 0.5in; }
    body { font-family: Arial, sans-serif; font-size: 10px; color: #111; margin: 0; }
    .header { margin-bottom: 12px; text-align: center; }
    .company { font-size: 14px; font-weight: bold; }
    .title { font-size: 16px; font-weight: bold; margin: 4px 0 8px; }
    .meta { font-size: 11px; margin-bottom: 2px; }
    .summary { display: flex; gap: 12px; margin: 14px 0; }
    .card { flex: 1; border: 1px solid #bbb; border-radius: 8px; padding: 10px 12px; text-align: center; }
    .card .label { font-size: 10px; font-weight: bold; letter-spacing: 0.04em; color: #444; }
    .card .value { font-size: 16px; font-weight: bold; margin-top: 4px; }
    table { width: 100%; border-collapse: collapse; font-size: 9px; }
    th, td { border: 1px solid #ccc; padding: 4px 3px; text-align: left; }
    th { background: #f3f3f3; font-weight: bold; }
    td.num, th.num { text-align: right; white-space: nowrap; }
    tfoot td { font-weight: bold; background: #fafafa; }
    .toolbar { margin-bottom: 12px; }
    .toolbar button { margin-right: 8px; padding: 6px 12px; }
    @media print { .toolbar { display: none; } }
  </style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">Print</button>
    <button onclick="window.close()">Close</button>
  </div>
  <div class="header">
    <div class="company">${company.companyName || ""}</div>
    <div class="title">Agent Expense Report</div>
    <div class="meta"><strong>Agent:</strong> ${agentLabel}</div>
    <div class="meta"><strong>Period:</strong> ${periodLabel}</div>
    <div class="meta"><strong>Printed:</strong> ${formatDateDisplay(new Date())}</div>
  </div>
  <div class="summary">
    <div class="card"><div class="label">TOTAL EXPENSES</div><div class="value">$${fmtMoney(summary?.total ?? totalTotal)}</div></div>
    <div class="card"><div class="label">APPLIED</div><div class="value">$${fmtMoney(summary?.applied ?? appliedTotal)}</div></div>
    <div class="card"><div class="label">BALANCE</div><div class="value">$${fmtMoney(summary?.balance ?? balanceTotal)}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>AE #</th>
        <th>Invoice #</th>
        <th>Invoice Date</th>
        <th>Applied Trades</th>
        ${OFFICE_EXPENSE_OPTIONS.map((o) => `<th class="num">${o.label}</th>`).join("")}
        <th class="num">HST</th>
        <th class="num">Total</th>
        <th class="num">Applied</th>
        <th class="num">Balance</th>
      </tr>
    </thead>
    <tbody>
      ${bodyRows || `<tr><td colspan="${12 + colKeys.length}" style="text-align:center;">No invoices in range</td></tr>`}
    </tbody>
    <tfoot>
      <tr>
        <td colspan="4">Totals (${(expenses || []).length} invoice${(expenses || []).length === 1 ? "" : "s"})</td>
        ${colKeys.map((k) => `<td class="num">$${fmtMoney(lineTotals[k])}</td>`).join("")}
        <td class="num">$${fmtMoney(hstTotal)}</td>
        <td class="num">$${fmtMoney(totalTotal)}</td>
        <td class="num">$${fmtMoney(appliedTotal)}</td>
        <td class="num">$${fmtMoney(balanceTotal)}</td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

      const w = window.open("", "_blank");
      if (!w) {
        toast.error("Allow pop-ups to print the report");
        return;
      }
      w.document.write(html);
      w.document.close();
    } catch (err) {
      console.error(err);
      toast.error("Failed to build expense report");
    }
  };

  const glHelpText = (() => {
    const hasComm = computed.lines.some(
      (l) => l.key === "commissionDeduction",
    );
    const hasOther = computed.lines.some(
      (l) => l.key !== "commissionDeduction",
    );
    if (hasOther && hasComm) {
      return "Other fees: Debit 12100 (total) / Credit selected G/L (amount) / Credit 23000 (HST). Commission deduction: Debit 12100 (amount) / Debit 23000 (HST) / Credit selected G/L (total).";
    }
    if (hasComm) {
      return "Commission deduction: Debit 12100 (amount) / Debit 23000 (HST) / Credit selected G/L (total).";
    }
    return "Other fees: Debit 12100 (total) / Credit selected G/L (amount) / Credit 23000 (HST). Bank charge & Garnishment: no tax.";
  })();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex">
        <FinanceSidebar />
        <div className="flex-1 p-6 space-y-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Agent Expenses</h1>
            <p className="text-sm text-gray-500 mt-1">
              Post recoverable agent expenses to A/R 12100. Applied updates after
              Agent Commission EFT.
            </p>
          </div>

          {/* Post form */}
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm max-w-3xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice date
                </label>
                <input
                  type="date"
                  value={form.invoiceDate}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, invoiceDate: e.target.value }))
                  }
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Defaults to today — change to override.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  AE Number
                </label>
                <input
                  type="text"
                  value={form.aeNumber}
                  readOnly
                  className="w-full rounded-md border-gray-300 bg-gray-50 shadow-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agent name
                </label>
                <select
                  value={form.agentId}
                  onChange={(e) => handleAgentChange(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm"
                >
                  <option value="">Select agent...</option>
                  {agents.map((a) => (
                    <option key={a._id || a.employeeNo} value={a.employeeNo}>
                      {a.employeeNo} — {a.firstName} {a.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice #
                </label>
                <input
                  type="text"
                  value={form.invoiceNumber}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, invoiceNumber: e.target.value }))
                  }
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  G/L Account
                </label>
                <select
                  value={form.glAccount}
                  onChange={(e) => handleGlChange(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm"
                >
                  <option value="">Select G/L Account</option>
                  {glOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Post date
                </label>
                <input
                  type="date"
                  value={form.postDate}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, postDate: e.target.value }))
                  }
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Defaults to today — change to override.
                </p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Office Expenses
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                Enter amounts to post a new Agent Expense invoice. Existing AE
                invoices for this agent appear in the balances tables below. On
                Agent Commission Info, selecting the agent fills Office Expenses
                with the remaining balance (you can override).
              </p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <label className="text-sm text-gray-700 whitespace-nowrap">
                  Tax rate (%)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={form.taxRate}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, taxRate: e.target.value }))
                  }
                  className="w-20 rounded-md border-gray-300 shadow-sm text-sm"
                />
                <span className="text-xs text-gray-500">
                  Applied to each expense (default{" "}
                  {Math.round(DEFAULT_OFFICE_EXPENSE_TAX_RATE * 100)}%). Not
                  applied to Bank charge or Garnishment.
                </span>
              </div>

              <div className="space-y-3">
                {OFFICE_EXPENSE_OPTIONS.map((opt) => {
                  const amount =
                    parseFloat(
                      String(form.amounts[opt.key] || "").replace(/,/g, ""),
                    ) || 0;
                  const taxExempt = isOfficeExpenseTaxExempt(opt.key);
                  const rate = parseOfficeExpenseTaxRate(form.taxRate);
                  const tax =
                    amount > 0 && !taxExempt ? round2(amount * rate) : 0;
                  const total = amount > 0 ? round2(amount + tax) : 0;
                  const bal = balances[opt.key];
                  return (
                    <div
                      key={opt.key}
                      className="flex flex-wrap items-center gap-3"
                    >
                      <span className="text-sm text-gray-800 w-44 shrink-0">
                        {opt.label}
                      </span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={form.amounts[opt.key]}
                        onChange={(e) =>
                          handleAmountChange(opt.key, e.target.value)
                        }
                        placeholder="Amount"
                        className="w-28 rounded-md border-gray-300 shadow-sm text-sm"
                      />
                      {amount > 0 && (
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                          {taxExempt
                            ? `(no tax) = $${roundTo2Str(total)}`
                            : `+ tax $${roundTo2Str(tax)} = $${roundTo2Str(total)}`}
                        </span>
                      )}
                      <span className="text-xs text-gray-500 ml-auto">
                        Posted ${fmtMoney(bal?.posted || 0)} · Remaining $
                        {fmtMoney(bal?.remaining || 0)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-gray-300 mt-4 pt-3 flex flex-wrap justify-end gap-6 text-sm">
                <div>
                  <span className="text-gray-500">Amount:</span>{" "}
                  <strong>${fmtMoney(computed.amount)}</strong>
                </div>
                <div>
                  <span className="text-gray-500">HST:</span>{" "}
                  <strong>${fmtMoney(computed.hst)}</strong>
                </div>
                <div>
                  <span className="text-gray-500">Total:</span>{" "}
                  <strong>${fmtMoney(computed.total)}</strong>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">{glHelpText}</p>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                type="button"
                disabled={posting || loading}
                onClick={handlePost}
                className="px-4 py-2 bg-blue-800 text-white rounded-md text-sm font-semibold hover:bg-blue-900 disabled:opacity-50"
              >
                {posting ? "Posting…" : "Post Expense"}
              </button>
            </div>
          </div>

          {/* Recent */}
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Recent Agent Expenses
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              Applied = office expenses recovered on trades after agent
              commission EFT. Balance = Total – Applied (remaining).
            </p>
            <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">AE #</th>
                    <th className="px-3 py-2 text-left">Invoice #</th>
                    <th className="px-3 py-2 text-left">Agent</th>
                    <th className="px-3 py-2 text-left">G/L</th>
                    <th className="px-3 py-2 text-left">Invoice Date</th>
                    <th className="px-3 py-2 text-left">Post Date</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Applied</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-3 py-6 text-center text-gray-500"
                      >
                        No agent expenses yet.
                      </td>
                    </tr>
                  ) : (
                    recent.map((row) => {
                      const bal = round2(
                        Number(row.total || 0) - Number(row.applied || 0),
                      );
                      return (
                        <tr
                          key={row._id || row.aeNumber}
                          className="border-t border-gray-100 odd:bg-white even:bg-gray-50"
                        >
                          <td className="px-3 py-2">{row.aeNumber}</td>
                          <td className="px-3 py-2">{row.invoiceNumber}</td>
                          <td className="px-3 py-2">{row.agentName}</td>
                          <td className="px-3 py-2">
                            {row.glAccount} {row.glAccountName}
                          </td>
                          <td className="px-3 py-2">
                            {formatDateDisplay(row.invoiceDate)}
                          </td>
                          <td className="px-3 py-2">
                            {formatDateDisplay(row.postDate)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            ${fmtMoney(row.total)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            ${fmtMoney(row.applied)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            ${fmtMoney(bal)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals by type + print */}
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Expense Totals by Type
            </h2>
            <p className="text-sm text-gray-500 mb-3">
              Total / Applied / Balance for each office expense, by agent.
              Applied updates after Agent Commission EFT (Commission Trust).
              Trades lists the trade number(s) where that Applied amount was
              recovered.
            </p>

            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
              <p className="font-semibold text-gray-800 mb-3">
                Print expense report
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Agent
                  </label>
                  <select
                    value={reportAgentId}
                    onChange={(e) => setReportAgentId(e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm text-sm min-w-[220px]"
                  >
                    <option value="">Select agent...</option>
                    {agents.map((a) => (
                      <option key={a._id || a.employeeNo} value={a.employeeNo}>
                        {a.employeeNo} — {a.firstName} {a.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    From (invoice date)
                  </label>
                  <input
                    type="date"
                    value={reportFrom}
                    onChange={(e) => setReportFrom(e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    To (invoice date)
                  </label>
                  <input
                    type="date"
                    value={reportTo}
                    onChange={(e) => setReportTo(e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm text-sm"
                  />
                </div>
                <button
                  type="button"
                  onClick={openPrintReport}
                  className="px-4 py-2 bg-blue-800 text-white rounded-md text-sm font-semibold hover:bg-blue-900"
                >
                  Print Report
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Leave dates empty to include all invoices for the selected
                agent. Print opens in portrait.
              </p>
            </div>

            <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left">Agent</th>
                    <th className="px-3 py-2 text-left">Expense</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2 text-right">Applied</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                    <th className="px-3 py-2 text-left">Trades</th>
                  </tr>
                </thead>
                <tbody>
                  {totalsByType.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-6 text-center text-gray-500"
                      >
                        No expense totals yet.
                      </td>
                    </tr>
                  ) : (
                    totalsByType.map((row, idx) => (
                      <tr
                        key={`${row.agentId}-${row.expenseKey}-${idx}`}
                        className="border-t border-gray-100"
                      >
                        <td className="px-3 py-2">{row.agentName}</td>
                        <td className="px-3 py-2">{row.expense}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          ${fmtMoney(row.total)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          ${fmtMoney(row.applied)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          ${fmtMoney(row.balance)}
                        </td>
                        <td className="px-3 py-2">{row.trades || ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentExpenses;
